import { SupabaseService } from './SupabaseService';

/**
 * SystemImageUploadService - Service for uploading system images (banners, news) to Supabase Storage
 */
export class SystemImageUploadService {
    private static readonly BUCKET_NAME = 'image_system_app';

    /**
     * Upload a single image file
     */
    static async uploadImage(
        file: File,
        folder: 'banners' | 'news' | 'other' = 'other'
    ): Promise<{ url: string | null; error: string | null }> {
        try {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                return {
                    url: null,
                    error: 'Vui lòng chọn file hình ảnh hợp lệ'
                };
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                return {
                    url: null,
                    error: 'Kích thước file không được vượt quá 5MB'
                };
            }

            const fileName = `${Date.now()}_${file.name}`;
            const filePath = `${folder}/${fileName}`;

            const { data, error } = await SupabaseService.uploadFile(
                this.BUCKET_NAME,
                filePath,
                file,
                {
                    contentType: file.type,
                    cacheControl: '3600',
                    upsert: false
                }
            );

            if (error) {
                console.error('Error uploading image:', error);
                return {
                    url: null,
                    error: 'Có lỗi xảy ra khi upload hình ảnh'
                };
            }

            if (data) {
                const publicUrl = SupabaseService.getPublicUrl(this.BUCKET_NAME, filePath);
                return {
                    url: publicUrl,
                    error: null
                };
            }

            return {
                url: null,
                error: 'Không thể lấy URL hình ảnh'
            };
        } catch (error) {
            console.error('Error uploading image:', error);
            return {
                url: null,
                error: 'Có lỗi xảy ra khi upload hình ảnh'
            };
        }
    }

    /**
     * Delete image from storage
     */
    static async deleteImage(imageUrl: string): Promise<boolean> {
        try {
            // Extract file path from public URL
            const bucketPath = `${this.BUCKET_NAME}/`;
            if (!imageUrl.includes(bucketPath)) {
                console.error('Invalid image URL for this bucket');
                return false;
            }

            const actualPath = imageUrl.split(bucketPath)[1].split('?')[0];

            const { error } = await SupabaseService.deleteFile(
                this.BUCKET_NAME,
                actualPath
            );

            if (error) {
                console.error('Error deleting image:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error deleting image:', error);
            return false;
        }
    }

    /**
     * Upload banner image
     */
    static async uploadBannerImage(file: File): Promise<{ url: string | null; error: string | null }> {
        return this.uploadImage(file, 'banners');
    }

    /**
     * Upload news image
     */
    static async uploadNewsImage(file: File): Promise<{ url: string | null; error: string | null }> {
        return this.uploadImage(file, 'news');
    }
}
